/**
 * המנוי של המשרד — what was purchased and what is used. 08 §6.0.1, §7, §12B.3.
 *
 * ── THIS SCREEN IS REACHABLE IN EVERY SUBSCRIPTION STATE, INCLUDING `suspended` ─────────────────
 *
 * 08 §6.0.1 makes billing screens exempt from the subscription gate, and the reason is a bug that
 * was already written once: without the exemption the gate returned 402 on the ONE screen whose
 * purpose is to resolve the 402 — and the Hebrew denial that says "speak to the office manager about
 * the subscription" pointed at a screen the office manager was also denied.
 *
 * So a suspended office still sees this. It sees WHY, and it sees exactly what it bought.
 *
 * ── WHAT IS NOT HERE ────────────────────────────────────────────────────────────────────────────
 *
 * No price, no invoice total, no discount. 08 §8.4 / CF-66: two tenants identical in plan, SKUs,
 * assignments and state and differing ONLY in a discount must produce byte-identical decisions —
 * *"if changing the price invalidated the entitlement cache, price would be part of entitlement."*
 * Money lives in the billing surface; capacity and entitlement live here, and the boundary between
 * them is the thing being protected.
 *
 * O-3 governs the rest: a subscription screen states what the office has. It does not sell.
 */

import { AppShell } from '../shell/AppShell';
import { useCallback } from 'react';
import { useWorkContext } from '../shell/ContextProvider';
import { CapacityPanel } from '../components/CapacityPanel';
import { FactList } from '../components/FactList';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { formatInstantHe, subscriptionStateHe } from '../api/adminVocabulary';
import { ServerRead } from '../components/ServerRead';
import { useServerData } from '../api/useServerData';
import { callOperation } from '../api/operations';
import { routes } from '../routes';

const TITLE_HE = 'המנוי של המשרד';

export function TenantSubscriptionScreen(): JSX.Element {
  const ctx = useWorkContext();
  /*
   * READ FROM THE SERVER. **Phase 2 §6, vertical B.**
   *
   * This screen used to render `fixtureSubscription`, which meant WHAT AN OFFICE IS TOLD IT IS
   * PAYING FOR — its plan, its state, its seat and company ceilings, which modules it holds — was
   * development data. An office reading `פעיל` off a fixture while its real subscription was
   * `past_due` would learn otherwise from a 402 on a screen it was in the middle of using.
   *
   * The route is `subscriptionExempt`, deliberately: 08 §6.0.1 keeps billing readable in EVERY
   * state, including suspended, because a customer who cannot pay must still be able to see why.
   * That exemption is worth nothing while the screen reads a local constant, and this is where it
   * starts being worth something.
   *
   * A 404 is `no_subscription` and is NOT the same as a refused read — `<ServerRead>` renders the
   * second, and the first arrives as `notFound` inside it with its own Hebrew.
   */
  const read = useCallback(() => callOperation('tenant.billing.read', {}), []);
  const subscriptionRead = useServerData(read);
  const back = { labelHe: 'ניהול המשרד', href: routes.tenantAdmin };

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="TENANT" actual={ctx.surface} />;
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={back}
      provenanceHe="המסך מציג את מה שנרכש ואת מה שבשימוש. סכומים, מחירים והנחות אינם חלק ממנו."
    >
      <ServerRead
        result={subscriptionRead.result}
        whatHe="את פרטי המנוי"
        skeletonRows={5}
        // 404 here is `no_subscription`, which is a state an office can actually be in — not an
        // error. It gets the words it had before this screen read anything.
        renderNotFound={() => (
          <StateBlock
            titleHe="אין מנוי להצגה"
            bodyHe="לא נמצא מנוי פעיל למשרד הזה."
            tone="unavailable"
            nextStep={<p>אם המצב הזה נשאר, יש לפנות לתמיכה.</p>}
          />
        )}
      >
        {(subscription) => {
          const state = subscriptionStateHe(subscription.state);
          return (
            <>
              <FactList
                facts={[
                  { labelHe: 'התכנית', value: subscription.planNameHe },
                  { labelHe: 'מצב המנוי', value: <StatusChip labelHe={state.labelHe} tone={state.tone} /> },
                  {
                    labelHe: 'תקופת המנוי',
                    value: (
                      <span dir="ltr">
                        {formatInstantHe(subscription.periodStartUtc)} — {formatInstantHe(subscription.periodEndUtc)}
                      </span>
                    ),
                  },
                  {
                    labelHe: 'סיום תקופת ניסיון',
                    value: formatInstantHe(subscription.trialEndsAtUtc),
                    absentHe: 'אין תקופת ניסיון',
                  },
                ]}
              />

              <CapacityPanel capacity={subscription.capacity} captionHe="מכסות — נרכש מול בשימוש" />

              <section className="tiles">
                {ctx.modules.map((m) => {
                  const purchased = subscription.licensedModules.includes(m.moduleCode);
                  return (
                    <article key={m.moduleCode} className="tile" data-available={purchased ? 'true' : 'false'}>
                      <h2 className="tile__title">{m.displayNameHe}</h2>
                      {/*
                        Purchased is not the same as available: the server resolves
                        Subscription → SKU → sku_module → module_code, and a company can be in the plan and
                        out of scope for one module. Both facts are shown, and neither is computed here.
                      */}
                      <p className="tile__lead">{purchased ? 'נרכש במנוי' : 'לא נרכש במנוי'}</p>
                      {m.catalogueStatus === 'active' ? null : (
                        <p className="tile__unavailable">החלק הזה טרם נפתח במערכת</p>
                      )}
                    </article>
                  );
                })}
              </section>

              <StateBlock
                titleHe="שינוי מסחרי"
                bodyHe="הרחבת מכסה, הוספת מודול או שינוי תכנית נעשים מול הפלטפורמה ואינם מתבצעים במסך הזה."
                tone="unavailable"
                nextStep={<p>ניתן לפנות למנהל המשרד או לתמיכה כדי לפתוח בקשת שינוי.</p>}
              />
            </>
          );
        }}
      </ServerRead>
    </AppShell>
  );
}
